import React, { useEffect, useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Toolbar,
  Box,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  AppBar,
  Typography,
  Chip,
} from '@mui/material';
import {
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  VpnKey as KeyIcon,
  Code as CodeIcon,
  People as PeopleIcon,
  PeopleAlt as PeopleAltIcon,
  ExpandLess,
  ExpandMore,
  Settings as SettingsIcon,
  Support as SupportIcon,
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  Groups as GroupsIcon,
  Chat as ChatIcon,
  Videocam as VideocamIcon,
  Mood as MoodIcon,
  PersonOff as PersonOffIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { themeBackgrounds } from '../theme/theme';
import { hasRouteAccess } from '../routes/rolePermissions';
import { useTour } from '../context/TourContext';

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path?: string;
  tourId?: string;
}

const expandedWidth: number = 300;
const collapsedWidth: number = 60;

export default function Sidebar(): React.JSX.Element {
  const theme = useTheme();
  const isMobile: boolean = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const { isTourOpen, currentStepData } = useTour();

  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [openBizOps, setOpenBizOps] = useState<boolean>(true);
  const [openChat, setChat] = useState<boolean>(true);
  const [openSentiment, setOpenSentiment] = useState<boolean>(true);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const handleDrawerToggle = (): void => setMobileOpen(!mobileOpen);
  const handleBizOpsClick = (): void => setOpenBizOps(!openBizOps);
  const handleChatClick = (): void => setChat(!openChat);
  const handleSentimentClick = (): void => setOpenSentiment(!openSentiment);
  const toggleCollapse = (): void => setIsCollapsed(!isCollapsed);
  const showChats = useSelector((state: any) => state.applicationDetails.is_chats_enabled);
  const showCalls = useSelector((state: any) => state.applicationDetails.is_calls_enabled);
  const navItems: NavItem[] = [
    { text: 'Home', icon: <HomeIcon />, path: '/home', tourId: 'tour-nav-home' },
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', tourId: 'tour-nav-dashboard' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/application-settings', tourId: 'tour-nav-settings' },
  ];
  const userRole: string = userDetails?.role || '';

  useEffect(() => {
    const expandSidebar = () => {
      setIsCollapsed(false);
      if (isMobile) setMobileOpen(true);
    };
    window.addEventListener('sampark-tour-expand-sidebar', expandSidebar);
    return () => window.removeEventListener('sampark-tour-expand-sidebar', expandSidebar);
  }, [isMobile]);

  const isTourTarget = (tourId?: string): boolean =>
    Boolean(isTourOpen && tourId && currentStepData?.target === tourId);

  const tourHighlightSx = (tourId?: string) =>
    isTourTarget(tourId)
      ? {
          backgroundColor: 'rgba(255, 152, 0, 0.18) !important',
          outline: '2px solid #ff9800',
          outlineOffset: -2,
          borderRadius: 1,
          zIndex: (t: any) => t.zIndex.modal + 12,
          position: 'relative' as const,
        }
      : {};

  const handleCallOptionClick = (text: string): void => {
    switch (text) {
      case 'Get Started':
        navigate('/get-started/calls');
        break;
      case 'Settings':
        navigate('/call-settings');
        console.log('Calls Settings clicked');
        break;
      default:
        console.log(`Unknown option: ${text}`);
    }
  };

  const handleSentimentOptionClick = (event: string): void => {
    switch (event) {
      case 'call-level':
        navigate('/sentiment-call-analytics');
        break;
      case 'participant-level':
        navigate('/sentiment-participant-analytics');
        break;
      case 'aggregate':
        navigate('/sentiment-aggregate-analytics');
        break;
      default:
        console.log(`Unknown option: ${event}`);
    }
  };

  const handleChatOptionClick = (text: string): void => {
    switch (text) {
      case 'Get Started':
        navigate('/get-started/chat');
        break;
      case 'Settings':
        navigate('/chat-settings');
        break;
      default:
        console.log(`Unknown option: ${text}`);
    }
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FFF',
        color: '#000',
      }}
    >
      <Toolbar>
        <Typography variant="h6" sx={{ flexGrow: 1, color: '#000' }}>
          Sampark
        </Typography>
      </Toolbar>
      <Box
        sx={{
          display: 'flex',
          justifyContent: isCollapsed ? 'center' : 'flex-end',
          alignItems: 'center',
          width: '100%',
          mb: 2,
        }}
      >
        {isCollapsed ? (
          <ChevronRight onClick={toggleCollapse} sx={{ cursor: 'pointer' }} />
        ) : (
          <ChevronLeft onClick={toggleCollapse} sx={{ cursor: 'pointer' }} />
        )}
      </Box>

      <Box
        sx={{
          overflowY: 'auto',
          flexGrow: 1,
          // '&::-webkit-scrollbar': {
          //   display: 'none'
          // },
          // '-ms-overflow-style': 'none',
          // 'scrollbar-width': 'none'
        }}
      >
        <List>
          {navItems
            .filter(({ path }) => !path || hasRouteAccess(userRole, path))
            .map(({ text, icon, path, tourId }) => (
            <ListItem key={text} disablePadding id={tourId}>
              <ListItemButton component={Link} to={path!} sx={tourHighlightSx(tourId)}>
                <ListItemIcon sx={{ color: '#000', minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                  {icon}
                </ListItemIcon>
                {!isCollapsed && <ListItemText primary={text} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {hasRouteAccess(userRole, '/sentiment-call-analytics') && <List>
          <ListItem disablePadding sx={{ marginTop: '-20px' }}>
            <ListItemButton onClick={handleSentimentClick}>
              <ListItemIcon sx={{ color: '#000', minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                <MoodIcon />
              </ListItemIcon>
              {!isCollapsed && <ListItemText primary="Syntalix AI" />}
			        {!isCollapsed && <Chip label="PRO" size="small" sx={{ ml: 1, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', height: '20px', fontSize: '0.7rem', fontWeight: 'bold', animation: 'blink 1s ease-in-out infinite', boxShadow: '0 0 10px rgba(240, 147, 251, 0.8)', '@keyframes blink': { '0%, 100%': { transform: 'scale(1)', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', boxShadow: '0 0 10px rgba(240, 147, 251, 0.8)' }, '50%': { transform: 'scale(1.1)', background: 'linear-gradient(135deg, #fa709a 0%, #8fd8fa 100%)', boxShadow: '0 0 20px rgba(250, 112, 154, 1)' } }, '& .MuiChip-label': { textShadow: '0 0 8px rgba(255, 255, 255, 0.8)' } }} />}
              {!isCollapsed && (openSentiment ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
          <Collapse in={openSentiment && !isCollapsed} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {[
                { text: 'Aggregate Analytics', event: 'aggregate', icon: <DashboardIcon /> },
                { text: 'Call Level Analytics', event: 'call-level', icon: <VideocamIcon /> },
                { text: 'Participant Level Analytics', event: 'participant-level', icon: <PeopleIcon /> },
              ].map(({ text, event, icon }) => (
                <ListItem key={text} disablePadding>
                  <ListItemButton sx={{ pl: 4 }} onClick={() => handleSentimentOptionClick(event)}>
                    <ListItemIcon sx={{ color: '#000', minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                      {icon}
                    </ListItemIcon>
                    {!isCollapsed && <ListItemText primary={text} />}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        </List>}
        <Divider sx={{ bgcolor: '#ccc' }} />
        <List>
          {[
            { text: 'Apps & Keysets', icon: <KeyIcon />, path: '/api-keys', tourId: 'tour-nav-api-keys' },
            { text: 'Members', icon: <PeopleAltIcon />, path: '/members', tourId: 'tour-nav-members' },
            { text: 'Participants', icon: <GroupsIcon />, path: '/application-participants', tourId: 'tour-nav-participants' },
            { text: 'Agents', icon: <GroupsIcon />, path: '/application-agents', tourId: 'tour-nav-agents' },
            { text: 'Chat Bot', icon: <ChatIcon />, path: '/chat-bot', tourId: 'tour-nav-chat-bot' },
            { text: 'AI Chat Bot', icon: <AutoAwesomeIcon />, path: '/ai-chat-bot' },
            { text: 'Inactive User Requests', icon: <PersonOffIcon />, path: '/inactive-user-requests' },
            { text: 'Call Recordings', icon: <VideocamIcon />, path: '/call-recordings', tourId: 'tour-nav-call-recordings' },
            { text: 'Documentation', icon: <CodeIcon />, path: '/documentation', tourId: 'tour-nav-documentation' },
            { text: 'Agent Chats', icon: <ChatIcon />, path: '/agents-chats' },
          ]
            .filter(({ path }) => !path || hasRouteAccess(userRole, path))
            .map(({ text, icon, path, tourId }) => (
              <ListItem key={text} disablePadding id={tourId}>
                <ListItemButton component={path ? Link : 'div'} to={path} sx={tourHighlightSx(tourId)}>
                  <ListItemIcon sx={{ color: '#000', minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                    {icon}
                  </ListItemIcon>
                  {!isCollapsed && <ListItemText primary={text} />}
                </ListItemButton>
              </ListItem>
            ))}
        </List>
        <Divider sx={{ bgcolor: '#ccc' }} />
        <List>
          {showCalls && <> <ListItem disablePadding>
            <ListItemButton onClick={handleBizOpsClick}>
              <ListItemIcon sx={{ color: '#000', minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                <VideocamIcon />
              </ListItemIcon>
              {!isCollapsed && <ListItemText primary="In App Calls" />}
              {!isCollapsed && (openBizOps ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
            <Collapse in={openBizOps && !isCollapsed} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {[
                  { text: 'Get Started', icon: <ChatIcon />, path: '/get-started/calls' },
                  { text: 'Settings', icon: <SettingsIcon />, path: '/call-settings' },
                ]
                  .filter(({ path }) => !path || hasRouteAccess(userRole, path))
                  .map(({ text, icon }) => (
                  <ListItem key={text} disablePadding>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => handleCallOptionClick(text)}>
                      <ListItemIcon sx={{ color: '#000', minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                        {icon}
                      </ListItemIcon>
                      {!isCollapsed && <ListItemText primary={text} />}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse></>}
          {showChats && <> <ListItem disablePadding>
            <ListItemButton onClick={handleChatClick}>
              <ListItemIcon sx={{ color: '#000', minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                <ChatIcon />
              </ListItemIcon>
              {!isCollapsed && <ListItemText primary="Chats" />}
              {!isCollapsed && (openChat ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>
            <Collapse in={openChat && !isCollapsed} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {[
                  { text: 'Get Started', icon: <ChatIcon />, path: '/get-started/chat' },
                  { text: 'Settings', icon: <SettingsIcon />, path: '/chat-settings' },
                ]
                  .filter(({ path }) => !path || hasRouteAccess(userRole, path))
                  .map(({ text, icon }) => (
                  <ListItem key={text} disablePadding>
                    <ListItemButton sx={{ pl: 4 }} onClick={() => handleChatOptionClick(text)}>
                      <ListItemIcon sx={{ color: '#000', minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                        {icon}
                      </ListItemIcon>
                      {!isCollapsed && <ListItemText primary={text} />}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse></>}
        </List>
      </Box>

      <Divider sx={{ bgcolor: '#ccc' }} />

      <Box sx={{ p: 2, textAlign: 'center' }}>
        {!isCollapsed && (
          <>
            <Button
              id="tour-upgrade-plan"
              variant="contained"
              fullWidth
              component={Link}
              to="/plans-and-billing"
              sx={{
                background: themeBackgrounds.default,
                color: '#ffffff',
                ...tourHighlightSx('tour-upgrade-plan'),
                '&:hover': {
                  background: themeBackgrounds.default,
                  opacity: 0.9,
                },
              }}
            >
              Upgrade Plan
            </Button>
            <Button
              id="tour-support-center"
              startIcon={<SupportIcon />}
              variant="outlined"
              color="info"
              fullWidth
              sx={{ mt: 2, ...tourHighlightSx('tour-support-center') }}
              component={Link}
              to="/support"
            >
              Support Center
            </Button>
          </>
        )}

      </Box>
    </Box>
  );

  return (
    <>
      {isMobile && (
        <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
          <Toolbar>
            <IconButton color="inherit" edge="start" onClick={handleDrawerToggle}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap>
              pubnub
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            [`& .MuiDrawer-paper`]: {
              width: expandedWidth,
              bgcolor: '#FFF',
              color: '#000',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: isCollapsed ? collapsedWidth : expandedWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: isCollapsed ? collapsedWidth : expandedWidth,
              transition: 'width 0.3s',
              overflowX: 'hidden',
              boxSizing: 'border-box',
              bgcolor: '#FFF',
              color: '#000',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
}
